import { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, Search, User, Loader2 } from 'lucide-preact'
import { leadsApi } from '@/api/leads'
import type { LeadMergeSearchResultDto, SearchForMergeParams } from '@/api/leads'
import { useToast } from '@/hooks/useToast'
import { BRAND_CLASSES } from '@/constants/theme'

interface SearchFormData {
  firstName: string
  lastName: string
  email: string
  companyName: string
  linkedInUrn: string
}

interface MergeContactModalProps {
  workspaceId: string
  campaignId: string
  currentContact: {
    id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    companyName?: string | null
  }
  onClose: () => void
  onMergeComplete: (mergeInfo: { survivorName: string; duplicateName: string }) => void
}

export const MergeContactModal: FunctionalComponent<MergeContactModalProps> = ({
  workspaceId,
  campaignId,
  currentContact,
  onClose,
  onMergeComplete
}) => {
  const { register, handleSubmit: handleFormSubmit, formState: { errors } } = useForm<SearchFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      linkedInUrn: ''
    }
  })

  const [searchResults, setSearchResults] = useState<LeadMergeSearchResultDto[]>([])
  const [selectedContact, setSelectedContact] = useState<LeadMergeSearchResultDto | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { showError } = useToast()

  const handleSearch = async (data: SearchFormData) => {
    const searchParams: SearchForMergeParams = {
      firstName: data.firstName.trim() || undefined,
      lastName: data.lastName.trim() || undefined,
      email: data.email.trim() || undefined,
      companyName: data.companyName.trim() || undefined,
      linkedInUrn: data.linkedInUrn.trim() || undefined,
      excludeId: currentContact.id
    }

    // Need at least one search criterion
    if (!searchParams.email && !searchParams.firstName && !searchParams.lastName && !searchParams.companyName && !searchParams.linkedInUrn) {
      showError('Search required', 'Please enter at least one search criterion')
      return
    }

    setIsSearching(true)
    try {
      const response = await leadsApi.searchForMerge(workspaceId, campaignId, searchParams)
      setSearchResults(response?.data || [])
    } catch {
      showError('Search failed', 'Could not search for contacts')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectContact = (contact: LeadMergeSearchResultDto) => {
    setSelectedContact(contact)
    setShowConfirmation(true)
  }

  const handleConfirmMerge = async () => {
    if (!selectedContact) return

    setIsMerging(true)
    try {
      await leadsApi.merge(workspaceId, campaignId, {
        DuplicateLeadId: currentContact.id,
        SurvivingLeadId: selectedContact.Id
      })

      // Pass merge details to parent
      onMergeComplete({
        survivorName: getContactDisplayName(selectedContact),
        duplicateName: getContactDisplayName(currentContact)
      })
    } catch {
      showError('Merge failed', 'Could not merge contacts. Please try again.')
      setIsMerging(false)
    }
  }

  const getContactDisplayName = (contact: LeadMergeSearchResultDto | typeof currentContact) => {
    // Handle both LeadMergeSearchResultDto (PascalCase) and currentContact (camelCase)
    let firstName: string | null | undefined
    let lastName: string | null | undefined
    let email: string | null | undefined

    if ('FirstName' in contact) {
      // LeadMergeSearchResultDto
      const dto = contact as LeadMergeSearchResultDto
      firstName = dto.FirstName
      lastName = dto.LastName
      email = dto.Email
    } else {
      // currentContact
      const current = contact as typeof currentContact
      firstName = current.firstName
      lastName = current.lastName
      email = current.email
    }

    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim()
    }
    return email || 'Unknown Contact'
  }

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            {showConfirmation ? 'Confirm Merge' : 'Merge Contact'}
          </h2>
          <button
            onClick={onClose}
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isMerging}
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        {showConfirmation ? (
          /* Confirmation Step */
          <div class="p-6 space-y-6">
            <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p class="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Warning:</strong> This action cannot be undone. The contact "{getContactDisplayName(currentContact)}"
                will be merged into "{getContactDisplayName(selectedContact!)}" and all associated data will be transferred.
              </p>
            </div>

            <div class="space-y-4">
              <div>
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contact Being Merged (will be deleted):</h3>
                <div class="bg-gray-50 dark:bg-gray-900 rounded p-3">
                  <div class="font-medium text-gray-900 dark:text-white">{getContactDisplayName(currentContact)}</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">{currentContact.email}</div>
                  {currentContact.companyName && (
                    <div class="text-sm text-gray-600 dark:text-gray-400">{currentContact.companyName}</div>
                  )}
                </div>
              </div>

              <div class="text-center text-gray-400">↓</div>

              <div>
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Merge Into (will survive):</h3>
                <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3">
                  <div class="font-medium text-gray-900 dark:text-white">{getContactDisplayName(selectedContact!)}</div>
                  <div class="text-sm text-gray-600 dark:text-gray-400">{selectedContact!.Email}</div>
                  {selectedContact!.CompanyName && (
                    <div class="text-sm text-gray-600 dark:text-gray-400">{selectedContact!.CompanyName}</div>
                  )}
                </div>
              </div>
            </div>

            <div class="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmation(false)}
                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                disabled={isMerging}
              >
                Back
              </button>
              <button
                onClick={handleConfirmMerge}
                disabled={isMerging}
                class="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isMerging && <Loader2 class="w-4 h-4 animate-spin" />}
                {isMerging ? 'Merging...' : 'Confirm Merge'}
              </button>
            </div>
          </div>
        ) : (
          /* Search Step */
          <>
            {/* Search Inputs */}
            <div class="p-6 border-b border-gray-200 dark:border-gray-700">
              <form onSubmit={handleFormSubmit(handleSearch)} class="space-y-3">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">First Name</label>
                    <input
                      type="text"
                      {...register('firstName', {
                        maxLength: { value: 100, message: 'First name must be 100 characters or less' }
                      })}
                      placeholder="Enter first name"
                      class={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                        errors.firstName
                          ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                          : `border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                      }`}
                    />
                    {errors.firstName && (
                      <p class="mt-1 text-xs text-red-600 dark:text-red-400">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Last Name</label>
                    <input
                      type="text"
                      {...register('lastName', {
                        maxLength: { value: 100, message: 'Last name must be 100 characters or less' }
                      })}
                      placeholder="Enter last name"
                      class={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                        errors.lastName
                          ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                          : `border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                      }`}
                    />
                    {errors.lastName && (
                      <p class="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                  <input
                    type="email"
                    {...register('email', {
                      maxLength: { value: 255, message: 'Email must be 255 characters or less' },
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' }
                    })}
                    placeholder="Enter email address"
                    class={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.email
                        ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                        : `border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                    }`}
                  />
                  {errors.email && (
                    <p class="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name</label>
                  <input
                    type="text"
                    {...register('companyName', {
                      maxLength: { value: 200, message: 'Company name must be 200 characters or less' }
                    })}
                    placeholder="Enter company name"
                    class={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.companyName
                        ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                        : `border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                    }`}
                  />
                  {errors.companyName && (
                    <p class="mt-1 text-xs text-red-600 dark:text-red-400">{errors.companyName.message}</p>
                  )}
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">LinkedIn URN</label>
                  <input
                    type="text"
                    {...register('linkedInUrn', {
                      maxLength: { value: 255, message: 'LinkedIn URN must be 255 characters or less' }
                    })}
                    placeholder="Enter LinkedIn URN"
                    class={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.linkedInUrn
                        ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                        : `border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                    }`}
                  />
                  {errors.linkedInUrn && (
                    <p class="mt-1 text-xs text-red-600 dark:text-red-400">{errors.linkedInUrn.message}</p>
                  )}
                </div>
                <div class="flex items-center justify-between gap-4">
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Searching for contacts similar to: <strong>{getContactDisplayName(currentContact)}</strong>
                  </p>
                  <button
                    type="submit"
                    disabled={isSearching}
                    class={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0 ${BRAND_CLASSES.BUTTON.PRIMARY}`}
                  >
                    {isSearching ? <Loader2 class="w-4 h-4 animate-spin" /> : <Search class="w-4 h-4" />}
                    {isSearching ? 'Searching...' : 'Search for Contacts'}
                  </button>
                </div>
              </form>
            </div>

            {/* Results */}
            <div class="flex-1 overflow-y-auto p-6">
              {isSearching ? (
                <div class="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 class="w-6 h-6 animate-spin mr-2" />
                  Searching...
                </div>
              ) : searchResults.length === 0 ? (
                <div class="text-center py-12 text-gray-500 dark:text-gray-400">
                  <p class="mb-2">No contacts found.</p>
                  <p class="text-xs">Enter search criteria above and click "Search for Contacts"</p>
                </div>
              ) : (
                <div class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Match</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {searchResults.map((contact) => (
                        <tr
                          key={contact.Id}
                          class="hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer transition"
                          onClick={() => handleSelectContact(contact)}
                        >
                          <td class="px-4 py-3">
                            <div class="flex items-center gap-3">
                              {contact.ProfilePicUrl ? (
                                <img
                                  src={contact.ProfilePicUrl}
                                  alt={getContactDisplayName(contact)}
                                  class="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                  <User class="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                              <div class="min-w-0 flex-1">
                                <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {getContactDisplayName(contact)}
                                </div>
                                <div class="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {contact.Email || '-'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {contact.CompanyName || '-'}
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {contact.JobTitle || '-'}
                          </td>
                          <td class="px-4 py-3">
                            <div class="flex items-center gap-1.5">
                              <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[80px]">
                                <div
                                  class={`h-2 rounded-full transition-all ${
                                    contact.MatchConfidencePercent >= 85
                                      ? 'bg-green-500'
                                      : contact.MatchConfidencePercent >= 60
                                        ? 'bg-yellow-500'
                                        : 'bg-red-500'
                                  }`}
                                  style={`width: ${contact.MatchConfidencePercent}%`}
                                />
                              </div>
                              <span class={`text-xs font-medium min-w-[32px] ${
                                contact.MatchConfidencePercent >= 85
                                  ? 'text-green-700 dark:text-green-400'
                                  : contact.MatchConfidencePercent >= 60
                                    ? 'text-yellow-700 dark:text-yellow-400'
                                    : 'text-red-700 dark:text-red-400'
                              }`}>
                                {contact.MatchConfidencePercent}%
                              </span>
                            </div>
                          </td>
                          <td class="px-4 py-3">
                            <button
                              class="px-3 py-1.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSelectContact(contact)
                              }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
